import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../shared/i18n';
import { familiesApi } from '../../api/guardians';

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'Asia/Ho_Chi_Minh', label: '(GMT+7) Ho Chi Minh' },
  { value: 'Asia/Bangkok', label: '(GMT+7) Bangkok' },
  { value: 'Asia/Singapore', label: '(GMT+8) Singapore' },
  { value: 'Asia/Tokyo', label: '(GMT+9) Tokyo' },
  { value: 'America/New_York', label: '(GMT-5) Eastern - New York' },
  { value: 'America/Los_Angeles', label: '(GMT-8) Los Angeles' },
  { value: 'Europe/London', label: '(GMT+0) London' },
  { value: 'Australia/Sydney', label: '(GMT+11) Sydney' },
];

export const FamilyForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [errors, setErrors] = useState({});

  // Detect user's timezone on mount
  useEffect(() => {
    if (!isEdit && !timeZone) {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Check if it's in our list, otherwise default to Ho Chi Minh
      const isKnownTz = TIMEZONE_OPTIONS.some(opt => opt.value === detectedTz);
      setTimeZone(isKnownTz ? detectedTz : 'Asia/Ho_Chi_Minh');
    }
  }, [isEdit, timeZone]);

  // Fetch existing family data if editing
  const { data: familyData } = useQuery({
    queryKey: ['family'],
    queryFn: () => familiesApi.getFamily(),
    enabled: isEdit,
  });

  useEffect(() => {
    if (familyData?.data?.family) {
      setName(familyData.data.family.name || '');
      setTimeZone(familyData.data.family.time_zone || 'Asia/Ho_Chi_Minh');
    }
  }, [familyData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => familiesApi.createFamily(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['dashboard']);
      // Store flash message and redirect
      sessionStorage.setItem('flash_notice', t('onboarding.family_form.created_success') || 'Family created successfully!');
      navigate('/');
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;
      if (errorData?.details) {
        setErrors({ general: errorData.details.join(', ') });
      } else {
        setErrors({ general: errorData?.message || 'An error occurred' });
      }
    },
  });

  // Helper to translate known API error messages
  const translateError = (message) => {
    const errorTranslations = {
      'Only family owners can perform this action': 'Bạn phải là chủ sở hữu gia đình mới có thể chỉnh sửa gia đình này.',
    };
    return errorTranslations[message] || message;
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => familiesApi.updateFamily(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['dashboard']);
      queryClient.invalidateQueries(['family']);
      sessionStorage.setItem('flash_notice', t('onboarding.family_form.updated_success') || 'Family updated successfully!');
      navigate('/');
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;
      if (errorData?.details) {
        setErrors({ general: errorData.details.join(', ') });
      } else {
        const errorMessage = translateError(errorData?.message || 'An error occurred');
        setErrors({ general: errorMessage });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Validate
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = t('common.error') || 'Name is required';
    }
    if (!timeZone) {
      newErrors.timeZone = t('common.error') || 'Time zone is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const data = { name: name.trim(), time_zone: timeZone };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                {isEdit
                  ? t('onboarding.family_form.edit_title')
                  : t('onboarding.family_form.create_title')}
              </h4>
            </div>
            <div className="card-body">
              {errors.general && (
                <div className="alert alert-danger" role="alert">
                  {errors.general}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    {t('onboarding.family_form.name_label')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    placeholder={t('onboarding.family_form.name_placeholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                  <div className="form-text">
                    {t('onboarding.family_form.name_help')}
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="time_zone" className="form-label">
                    {t('onboarding.family_form.timezone_label')}
                  </label>
                  <select
                    id="time_zone"
                    className={`form-select ${errors.timeZone ? 'is-invalid' : ''}`}
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    disabled={isSubmitting}
                  >
                    <option value="">Select time zone...</option>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {errors.timeZone && (
                    <div className="invalid-feedback">{errors.timeZone}</div>
                  )}
                  <div className="form-text">
                    {t('onboarding.family_form.timezone_help')}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-grow-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? (isEdit
                          ? t('onboarding.family_form.updating')
                          : t('onboarding.family_form.creating'))
                      : (isEdit
                          ? t('onboarding.family_form.submit_update')
                          : t('onboarding.family_form.submit_create'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyForm;
